// Module registry: the single list of screens. The sidebar, phone tab bar, command palette,
// router and route guards all read from here. Add a module = add one entry (see
// docs/FRONTEND-CONVENTIONS.md). `roles` is the default access; a later server-side
// policy can narrow it per user without changing this file's shape.
import type { LucideIcon } from 'lucide-react';
import {
  BadgeDollarSign,
  BookOpen,
  ChartLine,
  FileSpreadsheet,
  Gauge,
  ShoppingBag,
  Tag,
  Target,
  Users,
  UserRound,
  Wallet,
  Factory,
  ClipboardList,
  CalendarRange,
  House,
  Banknote,
  SlidersHorizontal,
} from 'lucide-react';
import { ALL_ROLES, PRODUCTION_ROLES, STAFF_ROLES, type Role } from './roles';

export type ModuleGroup = 'Overview' | 'Sales & Customers' | 'Production' | 'Finance' | 'Tools';
export const GROUP_ORDER: ModuleGroup[] = ['Overview', 'Sales & Customers', 'Production', 'Finance', 'Tools'];
export const GROUP_ICONS: Record<ModuleGroup, LucideIcon> = {
  Overview: House,
  'Sales & Customers': ShoppingBag,
  Production: Factory,
  Finance: Banknote,
  Tools: SlidersHorizontal,
};

export interface ModuleTab {
  id: string;
  title: string;
}

export interface ModuleDef {
  key: string;
  label: string;
  path: string;
  group: ModuleGroup;
  icon: LucideIcon;
  subtitle: string;
  /** 'ready' = built screen; 'placeholder' = routed page listing the planned KPIs. */
  status: 'ready' | 'placeholder';
  roles: readonly Role[];
  tabs?: ModuleTab[];
  /** Planned KPIs and views from design/spec-v2.md, shown on placeholder pages. */
  kpis: string[];
  /** Where the planned KPI list comes from (placeholder card subtitle). */
  kpiSource?: string;
  note?: string;
  keywords?: string[];
}

export const MODULES: ModuleDef[] = [
  {
    key: 'executive',
    label: 'Executive Summary',
    path: '/overview',
    group: 'Overview',
    icon: Gauge,
    subtitle: 'How is the business doing right now, and what needs my attention?',
    status: 'ready',
    roles: ALL_ROLES,
    kpis: [
      'Net sales vs compare-to',
      'Invoice margin %',
      'Operating income vs budget',
      'Open order backlog $',
      'B2B on-time-in-full %',
      'AR past due 60+ days $',
      'Estimated inventory + days of cover',
      'Cash collected',
    ],
    keywords: ['home', 'summary', 'kpi', 'today'],
  },
  {
    key: 'plan',
    label: 'Plan & Forecast',
    path: '/plan',
    group: 'Overview',
    icon: Target,
    subtitle: 'Where will we land, and how does that compare with plan?',
    status: 'placeholder',
    roles: ALL_ROLES,
    note: 'Data source to be decided. Plan scenarios are not in SAP Business One; the mockup used an illustrative plan.',
    kpis: [
      'Scenarios: actual vs Plan / Reforecast / Stretch / Downside by month and channel family',
      'Q4 outlook: projection Oct–Dec with band, method explained',
      'Variance bridge: plan to actual by channel family (waterfall)',
    ],
    keywords: ['forecast', 'budget', 'scenario', 'q4'],
  },
  {
    key: 'sales',
    label: 'Sales Performance',
    path: '/sales',
    group: 'Sales & Customers',
    icon: ChartLine,
    subtitle: 'Are we on plan, and which channels and products are driving it?',
    status: 'ready',
    roles: ALL_ROLES,
    tabs: [
      { id: 'overview', title: 'Overview' },
      { id: 'channels', title: 'Channels' },
      { id: 'products', title: 'Products' },
      { id: 'geography', title: 'Geography' },
    ],
    kpis: ['Net sales', 'Invoice margin', 'Invoices', 'Customers', 'Channel scorecard vs plan'],
    keywords: ['revenue', 'channels', 'scorecard', 'formats', 'skus', 'countries'],
  },
  {
    key: 'customers',
    label: 'Customers & Accounts',
    path: '/customers',
    group: 'Sales & Customers',
    icon: Users,
    subtitle: 'Who are our biggest accounts, and who has gone quiet?',
    status: 'placeholder',
    roles: ALL_ROLES,
    kpis: [
      'Top accounts ranked, Pareto concentration',
      'At-risk accounts: days since last invoice > 1.5 × average gap',
      'New and repeat customers by channel',
      'Account card: revenue trend, cadence, open orders, AR, last invoices, price list',
    ],
    keywords: ['accounts', 'at risk', 'pareto', 'new'],
  },
  {
    key: 'team',
    label: 'Sales Team',
    path: '/team',
    group: 'Sales & Customers',
    icon: UserRound,
    subtitle: 'How is each rep and territory performing?',
    status: 'placeholder',
    roles: STAFF_ROLES,
    kpis: [
      'Rep leaderboard: revenue, gross profit, accounts, invoices',
      'Attainment vs illustrative quota',
      'Territory table',
      '"No salesperson" share (data quality)',
    ],
    keywords: ['reps', 'quota', 'territory'],
  },
  {
    key: 'pricing',
    label: 'Pricing',
    path: '/pricing',
    group: 'Sales & Customers',
    icon: Tag,
    subtitle: 'Are we getting list price, and where do discounts go?',
    status: 'placeholder',
    roles: STAFF_ROLES,
    kpis: [
      'Price realization vs price list',
      'Discount depth',
      'Lines more than 5% off list by customer and item group',
    ],
    keywords: ['discount', 'realization', 'price list'],
  },
  {
    key: 'dtc',
    label: 'Direct-to-Consumer',
    path: '/dtc',
    group: 'Sales & Customers',
    icon: ShoppingBag,
    subtitle: 'How are harney.com and Amazon doing?',
    status: 'placeholder',
    roles: ALL_ROLES,
    kpis: [
      'harney.com orders, revenue, AOV',
      'New vs returning consumers',
      'Tea of the Month revenue (not derivable yet)',
      'Amazon channel summary',
      'Web traffic, ad spend, email: connect later (outside SAP)',
    ],
    keywords: ['harney.com', 'web', 'amazon', 'consumer'],
  },
  {
    key: 'production.demand_workbench',
    label: 'Demand Workbench',
    path: '/production/demand-workbench',
    group: 'Production',
    icon: ClipboardList,
    subtitle: 'Open demand vs stock vs planned production, build from demand',
    status: 'placeholder',
    roles: PRODUCTION_ROLES,
    note: 'Built in a later slice from the harney-production-calendar mockups.',
    kpiSource: 'From the production calendar mockups',
    kpis: [
      'Open sales order demand by item and due week',
      'Stock on hand and planned production against that demand',
      'Shortfall per item: what still has to be made',
      'Build from demand: turn a shortfall into planned production',
    ],
    keywords: ['production', 'demand', 'mrp', 'shortfall', 'planning'],
  },
  {
    key: 'production.week_board',
    label: 'Week Board',
    path: '/production/week-board',
    group: 'Production',
    icon: CalendarRange,
    subtitle: 'Fortnight production plan by lane and shift, cards become SAP production orders',
    status: 'placeholder',
    roles: PRODUCTION_ROLES,
    note: 'Built in a later slice from the harney-production-calendar mockups.',
    kpiSource: 'From the production calendar mockups',
    kpis: [
      'Two-week board by production lane and shift',
      'Production cards: item, quantity, lane, shift',
      'Release a card as an SAP production order',
      'Capacity per lane and shift',
    ],
    keywords: ['production', 'schedule', 'calendar', 'lanes', 'shifts', 'production order'],
  },
  {
    key: 'pnl',
    label: 'Profit & Loss',
    path: '/pnl',
    group: 'Finance',
    icon: BadgeDollarSign,
    subtitle: 'What did we earn, month by month?',
    status: 'placeholder',
    roles: STAFF_ROLES,
    kpis: [
      'Monthly P&L: revenue, COGS, gross margin, opex groups, operating income',
      'Book gross margin % trend',
      'Waterfall revenue to operating income',
      'Actual vs illustrative budget',
    ],
    keywords: ['p&l', 'income statement', 'opex', 'cogs'],
  },
  {
    key: 'cash',
    label: 'Cash & Working Capital',
    path: '/cash',
    group: 'Finance',
    icon: Wallet,
    subtitle: 'Where is cash tied up, and how fast does it come back?',
    status: 'placeholder',
    roles: STAFF_ROLES,
    kpis: [
      'AR aging (current, 1–30, 31–60, 61–90, 90+), DSO, top past-due accounts',
      'AP aging, DPO',
      'DIO and cash conversion cycle',
      'Collections by week, payments out',
    ],
    keywords: ['ar', 'ap', 'dso', 'dpo', 'collections'],
  },
  {
    key: 'reports',
    label: 'Reports',
    path: '/reports',
    group: 'Tools',
    icon: FileSpreadsheet,
    subtitle: 'Every dataset as a downloadable report.',
    status: 'placeholder',
    roles: ALL_ROLES,
    kpis: ['Library of every dataset (CSV / Excel)', 'Schedule a report (later)'],
    keywords: ['export', 'download', 'excel', 'csv'],
  },
  {
    key: 'glossary',
    label: 'KPI Glossary',
    path: '/glossary',
    group: 'Tools',
    icon: BookOpen,
    subtitle: 'What each number means, how it is calculated and where it lives in SAP.',
    status: 'placeholder',
    roles: ALL_ROLES,
    kpis: ['Searchable definitions', 'How calculated', 'Where in SAP Business One'],
    keywords: ['definitions', 'help', 'kpi'],
  },
];

export const moduleByKey = (key: string) => MODULES.find((m) => m.key === key);
export const canAccess = (m: ModuleDef, role: Role | null | undefined) => !!role && m.roles.includes(role);
export const modulesFor = (role: Role | null | undefined) => MODULES.filter((m) => canAccess(m, role));
export const HOME_PATH = '/overview';
